import React from "react";
import { Dialog, DialogContent, Typography, Box, Button } from "@mui/material";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifi1Bar";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AMBER, BORDER, LABEL } from "../../Global/colors";

const ERROR = "#ef5350";

const ConnectionBlockingDialog = ({ open, connectionDetails, onRetry, lastChecked }) => {
  if (!open) return null;

  const getIcon = () => {
    if (!connectionDetails.networkStatus) return <WifiOffIcon sx={{ fontSize: 44, color: ERROR }} />;
    if (!connectionDetails.internetConnection) return <SignalWifiOffIcon sx={{ fontSize: 44, color: ERROR }} />;
    return <CloudOffIcon sx={{ fontSize: 44, color: ERROR }} />;
  };

  const getTitle = () => {
    if (!connectionDetails.networkStatus) return "No network connection";
    if (!connectionDetails.internetConnection) return "No internet access";
    if (!connectionDetails.isApiConnected) return "Server unreachable";
    return "Connection failed";
  };

  const getMessage = () => {
    if (!connectionDetails.networkStatus)
      return "Check your WiFi or mobile data and try again.";
    if (!connectionDetails.internetConnection)
      return "Connected to a network but can't reach the internet.";
    if (!connectionDetails.isApiConnected)
      return "Server is unavailable. This may be temporary — please retry.";
    return "Multiple connection issues detected. Check your network.";
  };

  const statusRows = [
    { label: "Network",  value: connectionDetails.networkStatus      ? "Online"        : "Offline",      ok: connectionDetails.networkStatus },
    { label: "Internet", value: connectionDetails.internetConnection  ? "Connected"     : "No access",    ok: connectionDetails.internetConnection },
    { label: "Server",   value: connectionDetails.isApiConnected      ? "Reachable"     : "Unreachable",  ok: connectionDetails.isApiConnected },
  ];

  return (
    <Dialog
      open={open}
      keepMounted={false}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#161B22",
          border: `1px solid ${BORDER}`,
          borderRadius: "16px",
          m: "16px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
        },
      }}
    >
      <DialogContent sx={{ p: "24px" }}>
        {/* Icon */}
        <Box sx={{
          width: 72, height: 72, borderRadius: "50%",
          backgroundColor: "rgba(239,83,80,0.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
          mx: "auto", mb: "16px",
        }}>
          {getIcon()}
        </Box>

        {/* Title */}
        <Typography sx={{
          color: "#fff", fontWeight: 700, fontSize: "1.05rem",
          textAlign: "center", mb: "6px",
        }}>
          {getTitle()}
        </Typography>

        {/* Message */}
        <Typography sx={{
          color: LABEL, fontSize: "0.82rem",
          textAlign: "center", mb: "20px", lineHeight: 1.5,
        }}>
          {getMessage()}
        </Typography>

        {/* Status rows */}
        <Box sx={{
          backgroundColor: "#0d1117",
          border: `1px solid ${BORDER}`,
          borderRadius: "10px",
          mb: "20px",
          overflow: "hidden",
        }}>
          {statusRows.map(({ label, value, ok }, i) => (
            <Box key={label} sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: "14px", py: "9px",
              borderBottom: i < statusRows.length - 1 ? `1px solid ${BORDER}` : "none",
            }}>
              <Typography sx={{ color: LABEL, fontSize: "0.8rem" }}>{label}</Typography>
              <Typography sx={{
                fontSize: "0.8rem", fontWeight: 600,
                color: ok ? "#22C55E" : ERROR,
              }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Last checked */}
        {lastChecked && (
          <Typography sx={{
            color: "rgba(255,255,255,0.2)", fontSize: "0.7rem",
            textAlign: "center", mb: "16px",
          }}>
            Last checked: {lastChecked.toLocaleTimeString()}
          </Typography>
        )}

        {/* Retry button */}
        <Button
          fullWidth
          variant="contained"
          onClick={onRetry}
          disableElevation
          startIcon={<RefreshIcon />}
          sx={{
            backgroundColor: AMBER, color: "#000", fontWeight: 700,
            fontSize: "0.95rem", py: "12px", borderRadius: "10px",
            textTransform: "none", border: "none",
            "&:hover": { backgroundColor: "#e6a832" },
          }}
        >
          Test Again
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectionBlockingDialog;
